import type { ResourcesConfig } from 'aws-amplify';
import { environment } from '../environments/environment';

/**
 * Amplify v6 Cognito config sourced from the active Angular environment.
 */
type RuntimeAuthOverride = Partial<{
  userPoolId: string;
  userPoolClientId: string;
  domain: string;
  redirectSignIn: string[];
  redirectSignOut: string[];
}>;

type WindowWithAuthOverride = Window & { __CASTOR_AUTH__?: RuntimeAuthOverride };

function getRuntimeAuthOverride(): RuntimeAuthOverride {
  if (typeof window === 'undefined') {
    return {};
  }

  return (window as WindowWithAuthOverride).__CASTOR_AUTH__ ?? {};
}

function resolveAuthConfig() {
  const runtime = getRuntimeAuthOverride();
  return {
    userPoolId: runtime.userPoolId?.trim() || environment.auth.userPoolId,
    userPoolClientId: runtime.userPoolClientId?.trim() || environment.auth.userPoolClientId,
    domain: runtime.domain?.trim() || environment.auth.domain,
    redirectSignIn: runtime.redirectSignIn ?? environment.auth.redirectSignIn,
    redirectSignOut: runtime.redirectSignOut ?? environment.auth.redirectSignOut,
  };
}

const auth = resolveAuthConfig();
const hasRequiredAuth = Boolean(auth.userPoolId && auth.userPoolClientId);
const hasOAuthConfig = Boolean(auth.domain && auth.redirectSignIn.length && auth.redirectSignOut.length);

export const amplifyConfig: ResourcesConfig = hasRequiredAuth
  ? {
      Auth: {
        Cognito: {
          userPoolId: auth.userPoolId,
          userPoolClientId: auth.userPoolClientId,
          loginWith: hasOAuthConfig
            ? {
                email: true,
                oauth: {
                  domain: auth.domain,
                  scopes: ['openid', 'email', 'profile'],
                  redirectSignIn: auth.redirectSignIn,
                  redirectSignOut: auth.redirectSignOut,
                  responseType: 'code',
                },
              }
            : { email: true },
        },
      },
    }
  : {};
